/**
 * ENVIRONMENT: PRODUCTION
 * MODULE: Detailed Consumer Master Profile & Audit Journal
 * PATH: src/routes/users/UserDetailss.js
 * COMPLIANCE: Digital Personal Data Protection (DPDP) Act 2023, RBI Customer Master Standards
 */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Spin,
  Button,
  Table,
  Tag,
  Descriptions,
  Typography,
  Tooltip,
  Avatar,
  Space,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  HomeOutlined,
  CopyOutlined,
  GlobalOutlined,
  LaptopOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { getUserDetails, getUserLoginHistory } from "../../requests/user";
import "./UserDetail.css";

const { Title, Text } = Typography;

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [loading, setLoading] = useState(true);

  // Login History State
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginLoading, setLoginLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch Consumer Details
  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getUserDetails(id);
      if (res) {
        setUser(res.data || null);
        setBaseUrl(res.baseUrl || "");
      }
    } catch (err) {
      message.error(err?.message || "Failed to load customer profile details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch Login History
  const fetchLoginHistory = useCallback(
    async (page = 1) => {
      try {
        setLoginLoading(true);
        const res = await getUserLoginHistory(id, page);
        if (res?.data?.status === 1) {
          setLoginHistory(res.data.data || []);
          setPagination({
            current: Number(res.data.pagination?.current_page) || 1,
            pageSize: Number(res.data.pagination?.per_page) || 10,
            total: Number(res.data.pagination?.total) || 0,
          });
        }
      } catch (err) {
        console.error("Login history fetch error:", err);
      } finally {
        setLoginLoading(false);
      }
    },
    [id]
  );

  // Unified Single Fetch Trigger
  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchLoginHistory(1);
    }
  }, [id, fetchUserDetails, fetchLoginHistory]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const loginColumns = [
    {
      title: "SR",
      width: 60,
      align: "center",
      render: (_, __, index) => (
        <span className="audit-sr">
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </span>
      ),
    },
    {
      title: "IP Address",
      dataIndex: "ip",
      key: "ip",
      render: (ip) => (
        <Space size="small">
          <span className="audit-ip">{ip || "N/A"}</span>
          {ip && (
            <Tooltip title="Copy IP">
              <CopyOutlined
                className="copy-audit-icon"
                onClick={() => copyToClipboard(ip, "IP Address")}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Client Device / Agent",
      dataIndex: "device",
      key: "device",
      render: (device) => (
        <div className="audit-device-wrap">
          <LaptopOutlined style={{ color: "#0284c7" }} />
          <span>{device || "Web Browser"}</span>
        </div>
      ),
    },
    {
      title: "Geographic Location",
      dataIndex: "address",
      key: "address",
      render: (address) => (
        <div className="audit-location-wrap">
          <GlobalOutlined style={{ color: "#10b981" }} />
          <span>{address || "Location Unavailable"}</span>
        </div>
      ),
    },
    {
      title: "Timestamp (IST)",
      dataIndex: "login_time",
      key: "login_time",
      render: (time) => (
        <span className="audit-timestamp">{time || "N/A"}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="detail-loading-center">
        <Spin size="large" tip="Loading consumer records..." />
      </div>
    );
  }

  // Aadhaar Profile Picture URL
  const aadharUrl =
    baseUrl && user?.aadhar_image ? `${baseUrl}/${user.aadhar_image}` : null;

  const isKycVerified = Number(user?.verify_status) === 1;

  return (
    <div className="consumer-detail-container">
      {/* Top Navigation & Profile Summary Card */}
      <Card className="profile-hero-card" bordered={false}>
        <div className="hero-flex-wrapper">
          <div className="hero-left-meta">
            <div className="avatar-wrapper">
              {aadharUrl ? (
                <img
                  src={aadharUrl}
                  alt={user?.fullname || "Consumer"}
                  className="profile-real-avatar"
                />
              ) : (
                <Avatar size={76} icon={<UserOutlined />} className="profile-fallback-avatar" />
              )}
            </div>

            <div className="profile-title-block">
              <div className="name-badge-row">
                <Title level={3} className="consumer-fullname">
                  {user?.fullname || "Consumer Account"}
                </Title>
                <Tag color="cyan" className="b2c-pill">
                  Direct B2C
                </Tag>
                <Tag
                  icon={isKycVerified ? <CheckCircleFilled /> : <ClockCircleFilled />}
                  color={isKycVerified ? "success" : "warning"}
                  className="kyc-hero-badge"
                >
                  {isKycVerified ? "e-KYC Verified" : "Verification Pending"}
                </Tag>
              </div>

              <div className="contact-quick-row">
                <span className="quick-item">
                  <MailOutlined className="quick-icon" />
                  {user?.email || "No Email"}
                </span>
                <span className="quick-item">
                  <PhoneOutlined className="quick-icon" />
                  +91 {user?.mobile || "N/A"}
                </span>
                <span className="quick-item">
                  <CalendarOutlined className="quick-icon" />
                  UID: {id}
                </span>
              </div>
            </div>
          </div>

          <div className="hero-right-actions">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              size="middle"
              className="back-btn"
            >
              Back to Directory
            </Button>
          </div>
        </div>
      </Card>

      {/* Main Profile Breakdown */}
      <Row gutter={[16, 16]} className="mt-16">
        {/* Identity & Regulatory e-KYC */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <div className="section-card-title">
                <SafetyCertificateOutlined style={{ color: "#059669", marginRight: 8 }} />
                Identity &amp; Regulatory e-KYC Record
              </div>
            }
            className="detail-section-card"
            bordered={false}
          >
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Permanent Account Number (PAN)">
                <Text copyable strong>{user?.pan_number || "Not Furnished"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Name on PAN">
                {user?.pan_name || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item label="Aadhaar ID Ref">
                <Text copyable>{user?.aadhar_number || "Not Furnished"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="UIDAI Verified Name">
                {user?.aadhar_name || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item label="Date of Birth">
                {user?.aadhar_dob || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Gender">
                {user?.aadhar_gender === "M"
                  ? "Male"
                  : user?.aadhar_gender === "F"
                  ? "Female"
                  : user?.aadhar_gender || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item label="Father's / Guardian's Name" span={2}>
                {user?.aadhar_fatherName || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item label="Compliance Audit State" span={2}>
                <Tag color={isKycVerified ? "green" : "orange"} style={{ fontWeight: 600 }}>
                  {isKycVerified ? "Verified (Full Transacting Access)" : "Pending Documents"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Residential & Postal Address */}
        <Col xs={24} lg={10}>
          <Card
            title={
              <div className="section-card-title">
                <HomeOutlined style={{ color: "#0284c7", marginRight: 8 }} />
                Residential &amp; Postal Address
              </div>
            }
            className="detail-section-card"
            bordered={false}
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Street / Full Address">
                {user?.address || user?.street || "No residential address recorded"}
              </Descriptions.Item>
              <Descriptions.Item label="City">
                {user?.city || user?.town || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="State / Province">
                {user?.state || user?.province || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Postal PIN Code">
                <Text strong>{user?.pin_code || user?.pincode || user?.postal_code || user?.zip || "N/A"}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Security & Access Audit Journal (Login History) */}
      <div className="mt-16">
        <Card
          title={
            <div className="section-card-title">
              <ClockCircleFilled style={{ color: "#7c3aed", marginRight: 8 }} />
              Security &amp; Session Audit Journal
            </div>
          }
          className="detail-section-card"
          bordered={false}
        >
          <Table
            rowKey={(record, index) => index}
            columns={loginColumns}
            dataSource={loginHistory}
            loading={loginLoading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page) => fetchLoginHistory(page),
              showTotal: (total) => `Total ${total} authentication records`,
            }}
            scroll={{ x: 900 }}
            className="enterprise-audit-table"
          />
        </Card>
      </div>
    </div>
  );
};

export default UserDetail;